/**
 * Zero playout delay on the guest, from a Chromium (Electron) host.
 *
 * The receiver's jitter buffer is scheduled by libwebrtc's `VCMTiming`. Its render-ASAP
 * path is taken only when the sender's `playout-delay` RTP header extension arrives with
 * `min = 0` and a small `max`; nothing settable from JavaScript on either end reaches that
 * path (`jitterBufferTarget` / `playoutDelayHint` only raise the minimum). The extension
 * value is written per frame by native code - which a browser sender cannot influence
 * through `RTCRtpSender` - EXCEPT through libwebrtc's `WebRTC-ForceSendPlayoutDelay` field
 * trial, read in `rtp_sender_video.cc` and substituted for whatever the encoder put in the
 * frame header. Electron's main process can set Chromium field trials before the app is
 * ready, so a Chromium host can put every video sender into gaming mode with one switch.
 * This is the mechanism the cloud-gaming services use from their native senders.
 *
 * Two pieces are needed for it to take effect on the guest:
 *
 * 1. The switch (`FORCE_SEND_PLAYOUT_DELAY_SWITCH`). Chrome offers the `playout-delay`
 *    extmap by default and Chromium's answer echoes it, so negotiation is already in place;
 *    the trial supplies the value.
 *
 * 2. No audio/video sync group (`separateAudioMsid`). Chrome's receiver computes
 *    `min_playout_delay = max(extension_min, jitterBufferTarget, sync_min)` and the zero
 *    path needs 0. `sync_min` comes from `RtpStreamsSynchronizer`, which pairs an audio and a
 *    video stream sharing an `msid` stream id and delays whichever is ahead - which would
 *    push a zero-delay video up to NetEq's audio delay (120-320 ms measured on Android) and
 *    quietly rebuild the buffer. peer-lite adds both tracks to one local MediaStream, so the
 *    host's local SDP has to give the audio section its own stream id. Lip-sync then drifts
 *    by whatever NetEq holds; for game streaming that is the right trade.
 *
 * Field shape this answers (2026-09-20/21, three sessions): jitter-buffer target ramping to
 * 280 ms on a link with 2 ms jitter, 0 % loss, 5 ms RTT, 60 fps decode, no keyframes.
 */

/** Chromium switch name; the value is the trial string. */
export const FORCE_SEND_PLAYOUT_DELAY_SWITCH = 'force-fieldtrials';

/** playout-delay values are 12-bit, in 10 ms units: 0..40950 ms. */
export const PLAYOUT_DELAY_MAX_MS = 40_950;

/**
 * The libwebrtc trial string for a forced sender playout delay. `min`/`max` in ms; the trial
 * parser reads them out of the group name, which is what libwebrtc's own tests do
 * (`WebRTC-ForcePlayoutDelay/min_ms:70,max_ms:90/`). Throws on a range the sender would
 * reject (`rtp_sender_video.cc` ignores out-of-range or out-of-order values silently).
 */
export function forceSendPlayoutDelayTrial(minMs: number, maxMs: number): string {
  for (const [name, v] of [['minMs', minMs], ['maxMs', maxMs]] as const) {
    if (!Number.isInteger(v) || v < 0 || v > PLAYOUT_DELAY_MAX_MS) {
      throw new RangeError(`${name} must be an integer in 0..${PLAYOUT_DELAY_MAX_MS}, got ${v}`);
    }
  }
  if (minMs > maxMs) throw new RangeError(`minMs (${minMs}) must not exceed maxMs (${maxMs})`);
  return `WebRTC-ForceSendPlayoutDelay/min_ms:${minMs},max_ms:${maxMs}/`;
}

/**
 * Render-ASAP without decode pacing: `max_ms:0` skips the zero-delay decoder's minimum
 * spacing between frames. Kept for A/B runs only - see
 * `FORCE_SEND_PLAYOUT_DELAY_LOW_LATENCY` for the value the host ships and why.
 */
export const FORCE_SEND_PLAYOUT_DELAY_ZERO = forceSendPlayoutDelayTrial(0, 0);

/**
 * Keep render-ASAP AND decode pacing. In libwebrtc VCMTiming, min=0 and max<=500ms return
 * a zero render timestamp, bypassing the adaptive jitter target. A positive max retains
 * MaxWaitingTime's minimum decode spacing (8ms by default); max=0 bypasses that too.
 *
 * This is NOT an 80ms NACK/retransmission buffer. WebKit's Cocoa video adapter also does
 * not forward libwebrtc's max_composition_delay_in_frames to its display layer, so raising
 * this value within the zero-delay range cannot smooth Safari presentation. Preserve the
 * bypass and address burst delivery/recovery separately instead of adding an audio/video
 * sync group or a receiver minimum that takes video out of render-ASAP.
 * Source: WebKit/Source/ThirdParty/libwebrtc/Source/webrtc/modules/video_coding/timing/timing.cc
 */
export const PLAYOUT_DELAY_LOW_LATENCY_MAX_MS = 80;

/**
 * NetEq minimum both Android and Safari request, in ms.
 *
 * A floor, not a cap: `jitterBufferTarget` can only raise the minimum. Audio and video
 * stay on separate stream ids, so this depth does not become video's sync minimum.
 */
export const SHARED_AUDIO_JITTER_BUFFER_TARGET_MS = 80;

/** The value the host ships: render-ASAP with decode pacing enabled. */
export const FORCE_SEND_PLAYOUT_DELAY_LOW_LATENCY = forceSendPlayoutDelayTrial(0, PLAYOUT_DELAY_LOW_LATENCY_MAX_MS);

/** Suffix appended to the audio section's msid stream id. */
export const AUDIO_MSID_SUFFIX = '-audio';

/**
 * Rewrite the audio m-section(s) of a local SDP so their `msid` stream id differs from the
 * video's. Handles both the media-level `a=msid:<stream> <track>` and the legacy
 * `a=ssrc:<n> msid:<stream> <track>` form. Video sections and everything else are untouched;
 * an SDP with no audio section, or no msid, comes back byte-identical. Idempotent.
 */
export function separateAudioMsid(sdp: string): string {
  if (!sdp || !sdp.includes('m=audio')) return sdp;
  const eol = sdp.includes('\r\n') ? '\r\n' : '\n';
  const lines = sdp.split(eol);
  let inAudio = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('m=')) {
      inAudio = line.startsWith('m=audio');
      continue;
    }
    if (!inAudio) continue;
    lines[i] = line
      .replace(/^(a=msid:)(\S+)(\s)/, (_m, prefix: string, stream: string, ws: string) => `${prefix}${suffixed(stream)}${ws}`)
      .replace(/^(a=ssrc:\d+ msid:)(\S+)(\s)/, (_m, prefix: string, stream: string, ws: string) => `${prefix}${suffixed(stream)}${ws}`);
  }
  return lines.join(eol);
}

function suffixed(streamId: string): string {
  // `-` is "no stream" in msid; leave it, and never double-suffix.
  if (streamId === '-' || streamId.endsWith(AUDIO_MSID_SUFFIX)) return streamId;
  return `${streamId}${AUDIO_MSID_SUFFIX}`;
}

/** The peer-metadata bits that decide which streaming path a guest gets. */
export type GuestPlayoutMetadata = { isApple?: boolean; isFirefox?: boolean };

/**
 * ENGINE GATE for the Chromium Opus SDP policy and audio jitter-buffer floor only.
 *
 * Safari/Firefox retain browser-written Opus settings and a zero requested audio floor.
 * The host keeps audio msid separation and the playout-delay extension for EVERY engine:
 * coupling those to this gate reintroduced audio-driven video buffering on Safari.
 * The sender's 0–80 ms trial preserves render-ASAP with decode pacing on both engines.
 */
export function usesLowLatencyPlayout(metadata: GuestPlayoutMetadata | undefined): boolean {
  return metadata?.isApple !== true && metadata?.isFirefox !== true;
}

/**
 * Remove the `playout-delay` extmap from every video section so the extension is not
 * negotiated - and therefore never written by the sender, forced value or not. Idempotent;
 * an SDP without the extmap comes back byte-identical.
 */
export function stripPlayoutDelayExtmap(sdp: string): string {
  if (!sdp || !sdp.includes('rtp-hdrext/playout-delay')) return sdp;
  const eol = sdp.includes('\r\n') ? '\r\n' : '\n';
  let inVideo = false;
  const kept = sdp.split(eol).filter((line) => {
    if (line.startsWith('m=')) {
      inVideo = line.startsWith('m=video');
      return true;
    }
    return !(inVideo && line.startsWith('a=extmap:') && line.includes('rtp-hdrext/playout-delay'));
  });
  return kept.join(eol);
}

/** True when the SDP negotiates the playout-delay header extension on a video section. */
export function sdpNegotiatesPlayoutDelay(sdp: string): boolean {
  if (!sdp) return false;
  const eol = sdp.includes('\r\n') ? '\r\n' : '\n';
  let inVideo = false;
  for (const line of sdp.split(eol)) {
    if (line.startsWith('m=')) {
      inVideo = line.startsWith('m=video');
      continue;
    }
    if (inVideo && line.startsWith('a=extmap:') && line.includes('rtp-hdrext/playout-delay')) return true;
  }
  return false;
}
